import { useMemo } from 'react';
import ReactModal from 'react-modal';
import { Button, TextInput, Title } from '@mantine/core';
import { ConfirmSendModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/ConfirmSendModal';
import { DocumentPickerTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/DocumentPickerTable';
import { DocumentRowEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/DocumentRowEditor';
import { SendSuccess } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/SendSuccess';
import { useSendDocumentsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/useSendDocumentsController';
import {
	EmailMessageField,
	PickerFooter,
	ModalContent,
	ModalHeader,
	PickerRegion,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/SendDocumentsModal.styled';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useMasterDocumentsQuery } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentsQuery';
import {
	DocumentWorkflowTemplateInfo,
	InvestorBulkActionDto,
} from '@dealstack/shared/lib/generated';

interface Props {
	selectedInvestorsIds: string[];
	bulkActionPayload?: InvestorBulkActionDto;
	totalRecipients?: number;
	onClose: () => void;
	onUploadDocument: () => void;
}

/** Step shell: pick documents from the programme's master-document list, confirm, then success. */
const SendDocumentsModalComponent = ({
	selectedInvestorsIds,
	bulkActionPayload,
	totalRecipients,
	onClose,
	onUploadDocument,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const controller = useSendDocumentsController({
		selectedInvestorsIds,
		bulkActionPayload,
		totalRecipients,
	});
	const { documentWorkflowTemplates } = useMasterDocumentsQuery({});

	const selectedTemplates = useMemo(
		() =>
			controller.selectedIds
				.map((id) => documentWorkflowTemplates.find((template) => template.id === id))
				.filter(
					(template): template is DocumentWorkflowTemplateInfo => template !== undefined
				),
		[controller.selectedIds, documentWorkflowTemplates]
	);

	const recipientCount = totalRecipients ?? selectedInvestorsIds.length;

	return (
		<ReactModal
			contentLabel="send-documents-modal"
			isOpen
			onRequestClose={onClose}
			// react-modal's own defaults make the content element full-bleed (`inset: 40px`),
			// which the global `.ReactModal__Content` centring rule then translates off-screen
			// by half its own size. Reset the box here so ModalContent below decides the size;
			// `transform` still comes from that global rule.
			style={{
				content: {
					// Override react-modal's own top/left/right/bottom longhands in place. Do
					// NOT use the `inset` shorthand: it is a new key, so Object.assign appends
					// it last and it wipes these values.
					top: '50%',
					left: '50%',
					right: 'auto',
					bottom: 'auto',
					padding: 0,
					border: 'none',
					background: 'transparent',
					overflow: 'visible',
				},
			}}
		>
			<ModalContent data-testid="send-documents-modal">
				<ModalHeader>
					{/* Figma: Sans/B3-H3 (30)/Bold — the theme's global heading weight is 800, so pin 600. */}
					<Title order={3} fw={600}>
						{tMipManage('send_documents.title')}
					</Title>
					{controller.step === 'pick' && (
						<Button onClick={onUploadDocument}>
							{tMipManage('send_documents.picker.upload_document')}
						</Button>
					)}
				</ModalHeader>
				{controller.step === 'pick' && (
					<>
						<PickerRegion>
							<DocumentPickerTable
								selectedIds={controller.selectedIds}
								onSelectionChange={controller.setSelectedIds}
								renderRowEditor={(template) => (
									<DocumentRowEditor template={template} />
								)}
							/>
						</PickerRegion>
						<PickerFooter>
							<EmailMessageField>
								{/* Figma shows the note as a placeholder-only field, no label above. */}
								<TextInput
									aria-label={tMipManage('send_documents.confirm.message_label')}
									placeholder={tMipManage(
										'send_documents.confirm.message_placeholder'
									)}
									value={controller.emailText}
									onChange={(event) =>
										controller.setEmailText(event.currentTarget.value)
									}
								/>
							</EmailMessageField>
							{/* No cancel here: the design leaves the footer to the note and the
							    send action, and the overlay click closes the modal. */}
							<Button
								data-testid="send-documents-continue"
								onClick={controller.goToConfirm}
								disabled={controller.selectedIds.length === 0}
							>
								{tMipManage('send_documents.picker.send_document')}
							</Button>
						</PickerFooter>
					</>
				)}
				{controller.step === 'confirm' && (
					<ConfirmSendModal
						documents={selectedTemplates}
						recipientCount={recipientCount}
						onBack={controller.goBackToPick}
						onSend={() => void controller.send()}
						isSending={controller.isSending}
					/>
				)}
				{controller.step === 'success' && (
					<SendSuccess
						documentCount={selectedTemplates.length}
						recipientCount={recipientCount}
						onSendAnother={controller.sendAnother}
						onClose={onClose}
					/>
				)}
			</ModalContent>
		</ReactModal>
	);
};

export { SendDocumentsModalComponent as SendDocumentsModal };
